<script setup lang="ts">
/**
 * @file Screen size aware drawer modal that renders OzDrawer on phone and OzPopoverModal on desktop. Used in Content picker.
 */
import OzDrawerDraggable from '@@/library/v4/components/OzDrawerDraggable.vue'
import OzDrawerStatic from '@@/library/v4/components/OzDrawerStatic.vue'
import OzPopoverModal, { isCompletePopoverAnchor } from '@@/library/v4/components/OzPopoverModal.vue'
import type { PopoverAnchor } from '@@/types'
import { computed } from 'vue'

const {
  isPhone = false,
  isDraggableDrawer = false,
  windowHeight = 0,
  popoverAnchor = null,
} = defineProps<{
  isPhone?: boolean
  isDraggableDrawer?: boolean
  /** Forwarded to the drawer variants, which require it. Unused by OzPopoverModal. */
  windowHeight?: number
  /**
   * Forwarded to OzPopoverModal. Desktop rendering waits until all required
   * coordinates have been measured; drawer variants do not use this prop.
   */
  popoverAnchor?: Partial<PopoverAnchor> | null
}>()

defineEmits(['scrim-click', 'scrim-esc', 'swipedown', 'swipeup', 'trigger-anchor-update'])

const resolvedPopoverAnchor = computed<PopoverAnchor | null>(() =>
  isCompletePopoverAnchor(popoverAnchor) ? popoverAnchor : null,
)
</script>

<script lang="ts">
export default {}
</script>

<template>
  <OzDrawerStatic
    v-if="isPhone && !isDraggableDrawer"
    v-bind="$attrs"
    :window-height="windowHeight"
    @scrim-click="$emit('scrim-click')"
    @scrim-esc="$emit('scrim-esc')"
  >
    <template v-if="$slots.header" #header><slot name="header" /></template>
    <template #body><slot name="body" /></template>
  </OzDrawerStatic>
  <OzDrawerDraggable
    v-else-if="isPhone && isDraggableDrawer"
    v-bind="$attrs"
    :window-height="windowHeight"
    @scrim-click="$emit('scrim-click')"
    @scrim-esc="$emit('scrim-esc')"
    @swipedown="$emit('swipedown', $event)"
    @swipeup="$emit('swipeup', $event)"
  >
    <template v-if="$slots.header" #header><slot name="header" /></template>
    <template #body><slot name="body" /></template>
  </OzDrawerDraggable>
  <OzPopoverModal
    v-else-if="resolvedPopoverAnchor"
    v-bind="$attrs"
    :popover-anchor="resolvedPopoverAnchor"
    @trigger-anchor-update="$emit('trigger-anchor-update')"
    @scrim-click="$emit('scrim-click')"
    @scrim-esc="$emit('scrim-esc')"
  >
    <template v-if="$slots.header" #header><slot name="header" /></template>
    <template #body><slot name="body" /></template>
  </OzPopoverModal>
</template>
